import { ApolloProvider, ApolloClient, InMemoryCache } from '@apollo/client';
import { loadableReady } from '@loadable/component';
import * as Sentry from '@sentry/react';
import { BrowserTracing } from '@sentry/tracing';
// @ts-ignore
import { createUploadLink } from 'apollo-upload-client';
import React from 'react';
import { hydrateRoot, Root } from 'react-dom/client';
import { unstable_HistoryRouter as HistoryRouter } from 'react-router-dom';
import { createBrowserHistory } from 'history';
import fetch from 'unfetch';
import { App } from './App';
import { NavigationBlockerProvider } from './components/context';
import { defaultLocale, Locale, LocaleProvider } from './i18n';

declare global {
  interface Window {
    acolyteRoot: Root;
    __ACOLYTE_BOOTSTRAP__?: { locale?: Locale };
  }
}

if (process.env.NODE_ENV === 'production') {
  Sentry.init({
    dsn:
      'https://c4c4f34b28e841eea056b7c5b5792b46@o4504498826379264.ingest.sentry.io/4504498829000704',
    integrations: [new BrowserTracing()],
    tracesSampleRate: 1.0,
  });
}

const client = new ApolloClient({
  cache: new InMemoryCache().restore((window as any).__D),
  link: createUploadLink({
    credentials: 'same-origin',
    fetch,
    headers: { 'Apollo-Require-Preflight': 'true' },
    uri: '/api',
  }),
});

loadableReady().then(() => {
  const history = createBrowserHistory();
  // React Router bundles the same history contract under a different type.
  const routerHistory = (history as unknown) as React.ComponentProps<
    typeof HistoryRouter
  >['history'];
  const content = (
    <NavigationBlockerProvider history={history}>
      <HistoryRouter history={routerHistory}>
        <ApolloProvider client={client}>
          <LocaleProvider
            initialLocale={
              window.__ACOLYTE_BOOTSTRAP__?.locale || defaultLocale
            }
          >
            <App />
          </LocaleProvider>
        </ApolloProvider>
      </HistoryRouter>
    </NavigationBlockerProvider>
  );

  if (process.env.NODE_ENV === 'production') {
    hydrateRoot(document.getElementById('root'), content);
  } else {
    if (window.acolyteRoot) {
      window.acolyteRoot.render(content);
    } else {
      window.acolyteRoot = hydrateRoot(
        document.getElementById('root'),
        content
      );
    }
  }
});

// @ts-ignore
if (module.hot) {
  // @ts-ignore
  module.hot.accept();
}
